import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { IonicModule, ModalController } from '@ionic/angular';
import { AppService } from '../../services/app.service';

import { StorageService } from '../../services/storage.service';
import { ConnectionStatus, Network } from '@capacitor/network';

@Component({
  selector: 'app-region-selection',
  templateUrl: './region-selection.component.html',
  styleUrls: ['./region-selection.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [IonicModule],
})
export class RegionSelectionComponent implements OnInit {
  auto_detected_region: string = '';

  regions = {
    ANZ: {
      region: 'ANZ',
      countries: ['Australia', 'New Zealand'],
      flags: ['au', 'nz'],
      description:
        'We automatically detected that you are in Australia or New Zealand. Please select ANZ.',
    },
    UK: {
      region: 'UK',
      countries: ['United Kingdom'],
      flags: ['gb'],
      description:
        'We automatically detected that you are in the United Kingdom. Please select UK.',
    },
  };

  constructor(
    private app: AppService,
    private modalCtrl: ModalController,
    private storage: StorageService
  ) {}

  ngOnInit(): void {
    // Check internet connectivity.
    Network.getStatus().then((status: ConnectionStatus): void => {
      if (status.connected) {
        // Get country by IP address.
        this.app.getCountryByIP().then((country: 'UK' | 'ANZ'): void => {
          if (country) {
            this.auto_detected_region = country;
            this.sortRegions();
          }
        });
      } else {
        // Device is offline. Get country from the device.
        this.app.getCountryFromDevice().then((country: 'UK' | 'ANZ'): void => {
          if (country) {
            this.auto_detected_region = country;
            this.sortRegions();
          }
        });
      }
    });
  }

  /**
   * A tracking function used to uniquely identify a region by its key.
   *
   * @param {number} index - The current index of the region in the list.
   * @param {any} region - The region object containing the key property.
   * @return {string} - The unique identifier (key) of the region.
   */
  trackByRegion(index: number, region: any): string {
    return region.key;
  }

  /**
   * Retrieves an array of objects representing the regions, where each object contains a `key` and corresponding `value`.
   *
   * @return {Array<{key: string, value: any}>} An array of objects, each containing a `key` (region name) and `value` (region data).
   */
  get getRegions(): { key: string; value: any }[] {
    const keys: string[] = Object.keys(this.regions);
    return keys.map((key: string): { key: string; value: any } => ({
      key: key,
      value: this.regions[key],
    }));
  }

  /**
   * Sorts the regions by placing the auto-detected region first, followed by all other regions.
   *
   * @return {void} Does not return any value as the method modifies the `regions` property directly.
   */
  sortRegions(): void {
    const sortedRegions: typeof this.regions = {} as typeof this.regions;

    // Make the auto-detected region the first one.
    if (this.regions[this.auto_detected_region]) {
      sortedRegions[this.auto_detected_region] =
        this.regions[this.auto_detected_region];
    }

    // Add all other regions.
    for (const key in this.regions) {
      if (key !== this.auto_detected_region) {
        sortedRegions[key] = this.regions[key];
      }
    }

    // Replace the regions with the sorted list.
    this.regions = sortedRegions;
  }

  /**
   * Handles the selection of a region, updates the application state,
   * stores the selected region persistently, and closes the modal.
   *
   * @param event
   * @param {string} region - The selected region to be set in the application state.
   * @return {Promise<void>} A promise that resolves once the region is updated, stored, and the modal is dismissed.
   */
  async onSelectRegion(event: any, region: string): Promise<void> {
    event.preventDefault();
    event.stopPropagation();
    this.app.region = region;
    this.storage.set('Region', this.app.region);
    await this.modalCtrl.dismiss(region);
  }
}
